// Generic trades / quote-request demo template.
// Covers tile, plumber, electrician, painter, cleaning, gardener, moving.

import { useState } from 'react'
import DemoLayout from './shared/DemoLayout'
import type { ChatPersona } from './shared/ChatWidget'
import { useT } from './shared/i18n-ctx'
import type { Dict } from './shared/i18n-ctx'
import { TRADES_CONFIGS } from './configs'

const dict: Dict = {
  demo_tag: { en: 'Demo · Quote request + portfolio', mk: 'Демо · Барање понуда + портфолио', gr: 'Demo · Αίτηση προσφοράς' },
  get_quote: { en: 'Get a free quote', mk: 'Бесплатна понуда', gr: 'Δωρεάν προσφορά' },
  choose_service: { en: 'Choose service', mk: 'Избери услуга', gr: 'Επιλογή υπηρεσίας' },
  describe: { en: 'Describe the job', mk: 'Опиши ја работата', gr: 'Περιγραφή εργασίας' },
  address: { en: 'Address / city', mk: 'Адреса / град', gr: 'Διεύθυνση / πόλη' },
  name: { en: 'Your name', mk: 'Твое име', gr: 'Όνομα' },
  phone: { en: 'Phone', mk: 'Телефон', gr: 'Τηλέφωνο' },
  photos_hint: { en: 'Upload photos (optional — helps us quote faster)', mk: 'Прикачи слики (опционално)', gr: 'Φωτογραφίες (προαιρετικό)' },
  submit: { en: 'Send request', mk: 'Испрати', gr: 'Αποστολή' },
  submitted: { en: '✓ Request received. We will call you back soon.', mk: '✓ Барањето е примено. Ќе ве јавиме.', gr: '✓ Η αίτηση παραλήφθηκε.' },
  features: { en: 'What\'s included', mk: 'Што вклучува', gr: 'Τι περιλαμβάνεται' },
  f1: { en: 'Instant quote request form on your website', mk: 'Формулар за понуда на твојот сајт', gr: 'Φόρμα προσφοράς' },
  f2: { en: 'Photo + video upload for accurate quotes', mk: 'Слика / видео за точни понуди', gr: 'Φωτο/βίντεο' },
  f3: { en: 'Before/after portfolio gallery', mk: 'Галерија пред / по', gr: 'Gallery πριν / μετά' },
  f4: { en: 'Quote → invoice pipeline, no paperwork', mk: 'Понуда → фактура, без хартија', gr: 'Προσφορά → τιμολόγιο' },
  f5: { en: 'Customer signature on job completion', mk: 'Потпис на клиентот по работа', gr: 'Υπογραφή πελάτη' },
  f6: { en: 'SMS updates at each stage', mk: 'СМС на секој чекор', gr: 'SMS σε κάθε στάδιο' },
  portfolio: { en: 'Recent work', mk: 'Неодамнешни проекти', gr: 'Πρόσφατα έργα' },
  sample: { en: 'Sample project', mk: 'Пример проект', gr: 'Δείγμα έργου' },
}

const SAMPLE_PHOTOS = [
  'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=70',
  'https://images.unsplash.com/photo-1484101403633-562f891dc89a?auto=format&fit=crop&w=600&q=70',
  'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=600&q=70',
  'https://images.unsplash.com/photo-1527689368864-3a821dbccc34?auto=format&fit=crop&w=600&q=70',
]

export default function TradesDemo({ slug }: { slug: string }) {
  const config = TRADES_CONFIGS[slug]
  if (!config) return <div className="p-10 text-center text-ink-100">No trades config for &quot;{slug}&quot;.</div>
  const persona: ChatPersona = {
    name: config.industry.en,
    kind: 'quote',
    items: config.services.map((s) => ({ name: s })),
  }
  return (
    <DemoLayout dict={dict} accent="from-amber-500 to-yellow-600" chatPersona={persona}>
      <Body slug={slug} />
    </DemoLayout>
  )
}

function Body({ slug }: { slug: string }) {
  const { t, lang } = useT()
  const config = TRADES_CONFIGS[slug]
  const [service, setService] = useState('')
  const [desc, setDesc] = useState('')
  const [address, setAddress] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <div className="bg-gradient-to-b from-ink-950 via-ink-950 to-ink-900">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <span className="chip bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-5">
          {config.icon} {t('demo_tag')}
        </span>
        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white">
          Demo · {config.industry[lang]}
        </h1>
        <p className="mt-4 text-lg text-ink-100 max-w-2xl">{config.tagline[lang]}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-emerald-400 text-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          {config.responseTime[lang]}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="card max-w-2xl">
          <h2 className="text-2xl font-bold text-white mb-4">{t('get_quote')}</h2>
          {submitted ? (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-4 text-center font-medium">
              {t('submitted')}
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4">
              <label>
                <span className="label">{t('choose_service')}</span>
                <select required className="input" value={service} onChange={(e) => setService(e.target.value)}>
                  <option value="">—</option>
                  {config.services.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="label">{t('describe')}</span>
                <textarea required rows={3} className="input" value={desc} onChange={(e) => setDesc(e.target.value)} />
              </label>
              <label>
                <span className="label">{t('address')}</span>
                <input required className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label>
                  <span className="label">{t('name')}</span>
                  <input required className="input" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label>
                  <span className="label">{t('phone')}</span>
                  <input required type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </label>
              </div>
              <div className="rounded-xl border border-dashed border-ink-800 p-4 text-sm text-ink-500 text-center">
                📷 {t('photos_hint')}
              </div>
              <button type="submit" className="btn-primary">{t('submit')}</button>
            </form>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6">{t('portfolio')}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SAMPLE_PHOTOS.map((src, i) => (
            <div key={i} className="relative overflow-hidden rounded-2xl border border-ink-800 aspect-[4/3]">
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent" />
              <div className="absolute bottom-2 left-3 text-xs text-ink-100 font-medium">
                {t('sample')} #{i + 1}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-2xl font-bold text-white mb-6">{t('features')}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {['f1', 'f2', 'f3', 'f4', 'f5', 'f6'].map((k) => (
            <div key={k} className="card flex gap-3 items-start">
              <span className="text-2xl">✓</span>
              <span className="text-ink-100">{t(k)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
